import { useCallback, useEffect, useRef, useState } from 'react'
import { useForm } from 'react-hook-form'
import { toast } from 'react-toastify'

import {
  Button,
  DrawerBody,
  DrawerContent,
  DrawerFooter,
  DrawerHeader
} from '@clientbase/clientbase-library'
import { yupResolver } from '@hookform/resolvers/yup'
import {
  Box,
  CircularProgress,
  Drawer,
  Stack,
  Tab,
  Tabs,
  Typography,
  useMediaQuery
} from '@mui/material'
import { DataService } from 'api/DataService'
import { useAuthContext } from 'context/AuthContext'
import { setCookie } from 'nookies'

import IconContainer from 'components/IconContainer/IconContainer'
import { buildSalesWhatsappUrl } from 'components/Modal/ModalNewPlansSmall/utils/buildSalesWhatsappUrl'
import Steps from 'components/Steps'

import {
  SAAS_CHECKOUT_OPEN_EVENT,
  SaasCheckoutOpenDetail,
  SaasPlanKey
} from 'utils/saas-checkout-event'
import { SESSION_COOKIE_OPTIONS } from 'utils/sessionCookie'
import { track } from 'utils/tracking'
import { toastFirstError } from 'utils/yupResolverWithToast'

import CardPayment from './components/CardPayment'
import CustomerForm from './components/CustomerForm'
import { CustomerFormData, customerSchema } from './components/customerSchema'
import PixPayment from './components/PixPayment'
import PlanStep, { BillingCycle } from './components/PlanStep'
import {
  createSaasCheckout,
  fetchSaasBilling,
  fetchSaasSubscription
} from './SaasCheckout.service'
import { SaasBilling, SaasSubscription } from './SaasCheckout.types'

/** Avisa as telas (ex.: Meu Plano) que a assinatura mudou e devem recarregar. */
export const SAAS_SUBSCRIPTION_CHANGED_EVENT =
  'clientbase:saas-subscription-changed'

type Step = 'loading' | 'plan' | 'data' | 'payment' | 'done' | 'support'

const POLL_INTERVAL = 5000
const PLAN_NAMES: Record<string, string> = {
  start: 'Start',
  grow: 'Grow',
  scale: 'Scale'
}

const formatMoney = (value?: number | null) =>
  (value ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })

// O PhoneInput espera o número com DDI. Cadastro antigo guarda só DDD + número
// (10 ou 11 dígitos): recebe o 55. Número que já tem DDI fica como está.
const withCountryCode = (phone?: string | null) => {
  const digits = (phone || '').replace(/\D/g, '')
  return digits.length === 10 || digits.length === 11 ? `55${digits}` : digits
}

// Chave de checkout -> plano: oferta e anuais ativam o plano base.
const planOf = (key: SaasPlanKey) => key.replace(/_(offer|annual)$/, '')

const SaasCheckout = () => {
  const { user, setUser } = useAuthContext()
  const isMobile = useMediaQuery('(max-width:600px)')

  const [open, setOpen] = useState(false)
  const [planKey, setPlanKey] = useState<SaasPlanKey>('grow')
  const [source, setSource] = useState('')
  const [step, setStep] = useState<Step>('loading')
  const [subscription, setSubscription] = useState<SaasSubscription | null>(
    null
  )
  const [billing, setBilling] = useState<SaasBilling | null>(null)
  const [kind, setKind] = useState<'new' | 'upgrade'>('new')
  const [tab, setTab] = useState<'pix' | 'card'>('pix')
  const [submitting, setSubmitting] = useState(false)
  const [cycle, setCycle] = useState<BillingCycle>('monthly')
  const [planActive, setPlanActive] = useState(false)
  const [setup, setSetup] = useState(false)
  const [setupAmount, setSetupAmount] = useState<number | null>(null)
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null)

  const customerForm = useForm<CustomerFormData>({
    mode: 'onChange',
    resolver: yupResolver(customerSchema)
  })
  const { handleSubmit, reset } = customerForm

  const plan = planOf(planKey)
  const planName = PLAN_NAMES[plan] || plan
  const planOption = subscription?.plans.find((option) => option.key === plan)
  const isUpgrade = subscription?.status === 'active'
  const isShared = !!subscription?.shared
  // Chave enviada ao backend: o anual tem link próprio na conta de cobrança.
  const checkoutKey: SaasPlanKey =
    cycle === 'annual' && planOption?.annual ? planOption.annual.key : planKey
  // Implantação (SC-11421): só em assinatura nova e no link que a comporta. A
  // oferta tem bonificação no link inteiro, que abateria a implantação.
  const setupOption =
    isUpgrade || planKey === 'start_offer'
      ? null
      : cycle === 'annual' && planOption?.annual
      ? planOption.annual.setup
      : planOption?.setup
  const withSetup = setup && !!setupOption

  const stopPolling = () => {
    if (pollRef.current) clearInterval(pollRef.current)
    pollRef.current = null
  }

  // Plano novo na sessão sem esperar o próximo login: atualiza o usuário e o
  // cookie, e relê o perfil (o DataService sincroniza bloqueio e capabilities).
  const refreshSession = useCallback(
    async (activePlan: string) => {
      setCookie(
        undefined,
        'nextauth.current_plan',
        activePlan,
        SESSION_COOKIE_OPTIONS
      )
      if (user)
        setUser({
          ...user,
          current_plan: activePlan as typeof user.current_plan
        })
      await DataService({ type: 'GET', url: '/v1/merchants/perfil' })
      window.dispatchEvent(new CustomEvent(SAAS_SUBSCRIPTION_CHANGED_EVENT))
    },
    [user, setUser]
  )

  const onPaid = useCallback(() => {
    setStep('done')
    track('saas_checkout_paid', { plan, kind, source, setup })
  }, [plan, kind, source, setup])

  // Acompanha a cobrança: quando paga, vai para o sucesso e espera a ativação do
  // plano (feita pelo backend logo depois do pagamento).
  useEffect(() => {
    // Com o plano já ativo não há mais o que acompanhar, e sem esta trava o
    // setUser do refreshSession recriaria o efeito e refaria a ativação em loop.
    if (!billing || planActive || (step !== 'payment' && step !== 'done'))
      return undefined
    stopPolling()
    pollRef.current = setInterval(async () => {
      const result = await fetchSaasBilling(billing.uuid)
      if (!result.ok) return
      if (result.data.status === 'paid' && step === 'payment') onPaid()
      if (result.data.plan_active) {
        stopPolling()
        setPlanActive(true)
        refreshSession(plan)
      }
    }, POLL_INTERVAL)
    return stopPolling
  }, [billing, step, plan, planActive, onPaid, refreshSession])

  const start = useCallback(
    async (key: SaasPlanKey) => {
      setStep('loading')
      setBilling(null)
      setPlanActive(false)
      setTab('pix')
      setCycle('monthly')
      setSetup(false)
      setSetupAmount(null)
      const result = await fetchSaasSubscription()
      if (!result.ok) {
        toast.error(result.message)
        setOpen(false)
        return
      }
      const data = result.data
      setSubscription(data)

      if (data.self_service === 'needs_support') {
        setStep('support')
        return
      }

      // Sem downsell: plano igual ou abaixo do atual não é contratável aqui.
      const option = data.plans.find((item) => item.key === planOf(key))
      if (option && !option.available && data.status !== 'pending') {
        toast.info('Esta conta já está neste plano ou em um plano superior.')
        setOpen(false)
        return
      }

      const pending = data.pending_checkout
      // Só retoma a cobrança pendente que ainda não venceu; vencida, o
      // backend cancela e gera outra a partir do passo do plano.
      if (
        pending &&
        planOf(pending.plan_key) === planOf(key) &&
        pending.reusable
      ) {
        if (pending.plan_key.endsWith('_annual')) setCycle('annual')
        setSetup(!!pending.setup)
        setSetupAmount(pending.setup_amount ?? null)
        setKind(pending.kind)
        setBilling(pending)
        setStep('payment')
        return
      }

      const address = data.customer?.address || {}
      reset({
        name: data.customer?.name || '',
        document: data.customer?.document || '',
        email: data.customer?.email || '',
        phone: withCountryCode(data.customer?.phone),
        postal_code: address.postal_code || '',
        street: address.street || '',
        number: address.number || '',
        complement: address.complement || '',
        neighborhood: address.neighborhood || '',
        city: address.city || '',
        state: address.state || ''
      })
      setKind(data.status === 'active' ? 'upgrade' : 'new')
      setStep('plan')
    },
    [reset]
  )

  useEffect(() => {
    const handleOpen = (event: Event) => {
      const detail = (event as CustomEvent<SaasCheckoutOpenDetail>).detail
      if (!detail?.planKey) return
      setPlanKey(detail.planKey)
      setSource(detail.source)
      setOpen(true)
      track('saas_checkout_started', {
        plan: planOf(detail.planKey),
        source: detail.source
      })
      start(detail.planKey)
    }
    window.addEventListener(SAAS_CHECKOUT_OPEN_EVENT, handleOpen)
    return () =>
      window.removeEventListener(SAAS_CHECKOUT_OPEN_EVENT, handleOpen)
  }, [start])

  const close = () => {
    stopPolling()
    setOpen(false)
    if (planActive)
      window.dispatchEvent(new CustomEvent(SAAS_SUBSCRIPTION_CHANGED_EVENT))
  }

  const submitCheckout = async (customer?: CustomerFormData) => {
    setSubmitting(true)
    try {
      const result = await createSaasCheckout(
        checkoutKey,
        customer && {
          name: customer.name,
          document: customer.document,
          email: customer.email,
          phone: customer.phone,
          address: {
            postal_code: customer.postal_code,
            street: customer.street,
            number: customer.number,
            complement: customer.complement,
            neighborhood: customer.neighborhood,
            city: customer.city,
            state: customer.state
          }
        },
        withSetup
      )

      if (!result.ok) {
        if (result.code === 'needs_support') {
          setStep('support')
          return
        }
        toast.error(result.message)
        return
      }

      setKind(result.data.kind)
      if (result.data.activated) {
        setPlanActive(true)
        setStep('done')
        refreshSession(result.data.plan)
        return
      }
      setSetupAmount(withSetup && setupOption ? setupOption.amount : null)
      setBilling(result.data.billing)
      setStep('payment')
    } catch {
      toast.error('Não foi possível gerar a cobrança. Tente novamente.')
    } finally {
      // Nunca deixa os botões presos: qualquer saída libera o formulário.
      setSubmitting(false)
    }
  }

  const continueFromPlan = () => {
    if (isUpgrade) submitCheckout()
    else setStep('data')
  }

  const stepLabels = isUpgrade
    ? ['Plano', 'Pagamento']
    : ['Plano', 'Seus dados', 'Pagamento']
  const activeStep = {
    loading: 0,
    support: 0,
    plan: 0,
    data: 1,
    payment: isUpgrade ? 1 : 2,
    done: stepLabels.length
  }[step]

  const renderPlan = () => (
    <PlanStep
      plan={plan}
      planName={planName}
      planKey={planKey}
      planOption={planOption}
      subscription={subscription}
      cycle={cycle}
      onCycleChange={(value) => {
        setCycle(value)
        track('saas_checkout_payment_method', { plan, cycle: value })
      }}
      setupOption={setupOption}
      withSetup={withSetup}
      onSetupChange={(value) => {
        setSetup(value)
        track('saas_checkout_setup_toggled', { plan, selected: value, source })
      }}
    />
  )

  const paymentLabel = () => {
    if (kind === 'upgrade') return 'Diferença proporcional do upgrade'
    const withImplantation = setup ? ' + implantação' : ''
    if (
      checkoutKey.endsWith('_annual') ||
      billing?.description?.match(/anual/i)
    )
      return `Anuidade do Plano ${planName}${withImplantation}`
    return `Primeira mensalidade do Plano ${planName}${withImplantation}`
  }

  const renderPayment = () =>
    billing && (
      <Stack gap={2}>
        <Box borderRadius="8px" p={2} bgcolor="#F2F5F2">
          <Stack gap={0.25}>
            <Typography variant="smLight" component="span" color="#646664">
              {paymentLabel()}
            </Typography>
            <Typography variant="lgBold" component="span" color="#231F20">
              {formatMoney(billing.amount)}
            </Typography>
            {setup && setupAmount ? (
              <Typography variant="smLight" component="span" color="#646664">
                Inclui {formatMoney(setupAmount)} da implantação, pagamento
                único. As próximas cobranças são só do plano.
              </Typography>
            ) : null}
          </Stack>
        </Box>

        <Tabs
          value={tab}
          onChange={(_, value) => {
            setTab(value)
            track('saas_checkout_payment_method', { plan, method: value })
          }}
          variant="fullWidth"
          sx={{ '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 } }}
        >
          <Tab value="pix" label="PIX" />
          <Tab value="card" label="Cartão de crédito" />
        </Tabs>

        {/* As duas abas ficam montadas: trocar de aba não pede o PIX de novo
            nem apaga o que já foi digitado no cartão. */}
        <Box hidden={tab !== 'pix'}>
          <PixPayment billingUuid={billing.uuid} />
        </Box>
        <Box hidden={tab !== 'card'}>
          <CardPayment
            billingUuid={billing.uuid}
            recurring={kind === 'new'}
            amount={billing.amount}
            maxInstallments={billing.max_installments}
            maxInstallmentsWithoutFee={billing.max_installments_without_fee}
            savedCardLast4={
              kind === 'upgrade' && !isShared
                ? subscription?.subscription?.card_last4
                : null
            }
            onPaid={onPaid}
          />
        </Box>
      </Stack>
    )

  const renderDone = () => (
    <Stack alignItems="center" gap={2} py={4} textAlign="center">
      {planActive ? (
        <>
          <IconContainer icon="check" />
          <Typography variant="lgBold" component="p" m={0}>
            Plano {planName} ativado
          </Typography>
          <Typography variant="smLight" component="p" m={0}>
            Os recursos do novo plano já estão liberados na sua conta.
          </Typography>
          {setup && (
            <Typography variant="smLight" component="p" m={0}>
              Nosso time vai entrar em contato para agendar a sua implantação.
            </Typography>
          )}
        </>
      ) : (
        <>
          <CircularProgress size={28} />
          <Typography variant="mdBold" component="p" m={0}>
            Pagamento confirmado
          </Typography>
          <Typography variant="smLight" component="p" m={0}>
            Estamos ativando o seu plano. Leva só alguns segundos.
          </Typography>
        </>
      )}
    </Stack>
  )

  const supportMessage = () => {
    if (subscription?.support_reason === 'non_monthly')
      return 'Sua assinatura é anual. Para trocar de plano antes da renovação, nosso time faz a alteração para você.'
    return 'A assinatura desta conta tem uma configuração especial (por exemplo, é cobrada junto com outras contas). Nosso time faz a alteração para você.'
  }

  const renderSupport = () => (
    <Stack gap={2} py={2}>
      <Typography variant="mdBold" component="p" m={0}>
        Fale com o nosso time para alterar o plano
      </Typography>
      <Typography variant="smLight" component="p" m={0}>
        {supportMessage()}
      </Typography>
    </Stack>
  )

  const footer = () => {
    if (step === 'plan')
      return (
        <>
          <Button
            type="button"
            variantButton="neutral"
            fullWidth
            onClick={close}
          >
            Cancelar
          </Button>
          <Button
            type="button"
            fullWidth
            onClick={continueFromPlan}
            disabled={submitting}
          >
            {submitting ? (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                component="span"
              >
                <CircularProgress size={16} color="inherit" />
                Gerando cobrança…
              </Stack>
            ) : isUpgrade ? (
              'Contratar upgrade'
            ) : (
              'Continuar'
            )}
          </Button>
        </>
      )
    if (step === 'data')
      return (
        <>
          <Button
            type="button"
            variantButton="neutral"
            fullWidth
            onClick={() => setStep('plan')}
            disabled={submitting}
          >
            Voltar
          </Button>
          <Button
            fullWidth
            type="button"
            disabled={submitting}
            onClick={handleSubmit(
              (data) => submitCheckout(data),
              toastFirstError
            )}
          >
            {submitting ? (
              <Stack
                direction="row"
                alignItems="center"
                gap={1}
                component="span"
              >
                <CircularProgress size={16} color="inherit" />
                Gerando cobrança…
              </Stack>
            ) : (
              'Ir para o pagamento'
            )}
          </Button>
        </>
      )
    if (step === 'support')
      return (
        <>
          <Button
            type="button"
            variantButton="neutral"
            fullWidth
            onClick={close}
          >
            Fechar
          </Button>
          <Button
            type="button"
            fullWidth
            onClick={() =>
              window.open(
                buildSalesWhatsappUrl(),
                '_blank',
                'noopener,noreferrer'
              )
            }
          >
            Falar com o suporte
          </Button>
        </>
      )
    return (
      <Button
        type="button"
        variantButton={step === 'done' ? 'primary' : 'neutral'}
        fullWidth
        onClick={close}
      >
        {step === 'done' ? 'Concluir' : 'Pagar depois'}
      </Button>
    )
  }

  return (
    <Drawer
      open={open}
      onClose={close}
      anchor="right"
      variant="temporary"
      PaperProps={{ sx: { width: isMobile ? '100vw' : '560px' } }}
    >
      {/* Sem <form> no drawer: os botões da lib são submit por padrão, e o
          "Continuar" do plano disparava o checkout por baixo. O formulário de
          cartão também não pode ficar aninhado em outro form. */}
      <DrawerBody>
        <DrawerHeader onClose={close}>
          <Stack direction="row" alignItems="center" gap={2}>
            <IconContainer icon="workspacePremium" />
            <Typography variant="mdBold">
              {isUpgrade
                ? `Upgrade para o Plano ${planName}`
                : `Contratar Plano ${planName}`}
            </Typography>
          </Stack>
        </DrawerHeader>

        <DrawerContent>
          <Stack gap={3} py={1}>
            {step !== 'support' && step !== 'loading' && (
              <Steps steps={stepLabels} activeStep={activeStep} />
            )}
            {step === 'loading' && (
              <Stack alignItems="center" py={6}>
                <CircularProgress size={28} />
              </Stack>
            )}
            {step === 'plan' && renderPlan()}
            {step === 'data' && <CustomerForm form={customerForm} />}
            {step === 'payment' && renderPayment()}
            {step === 'done' && renderDone()}
            {step === 'support' && renderSupport()}
          </Stack>
        </DrawerContent>

        {step !== 'loading' && <DrawerFooter>{footer()}</DrawerFooter>}
      </DrawerBody>
    </Drawer>
  )
}

export default SaasCheckout
